import { Form } from '@/shared/form/form';
import { classInstance, debounce } from '@/shared/helpers/helpers';
import { initSearchResults } from './init-search-results';

export class Search {
	constructor(selector, options = {}, callbacks = {}) {
		this.$container = selector;
		if (!this.$container) return;

		this.$container.search = this;
		classInstance.set(this.$container, { search: this });

		this.minQueryLength = options.minQueryLength || 3;
		this.dataUrl = options.dataUrl || '/search';
		this.debounceDelay = options.debounceDelay || 300;
		this.requestMode = options.requestMode || 'auto';
		this.postHeaders = options.postHeaders;
		this.postBody = options.postBody;
		this.searchProvider = options.searchProvider || null;
		this.callbacks = callbacks;

		this.selectorField = options.selectorField || '.js-search__field';
		this.selectorResult = options.selectorResult || '.js-search-result';
		this.selectorClearResult = options.selectorResult || '.js-search__clear-result';
		this.selectorResultHistory = options.selectorResultHistory || '.js-search-history';
		this.selectorClearHistory = options.selectorClearHistory || '.js-search-clear-history';
		this.selectorSearchItem = options.selectorSearchItem || '.js-search-item';
		this.selectorRemoveItemButton = options.selectorRemoveItemButton || '.js-search-item__remove';

		this.requestCounter = 0;
		this.abortController = null;

		this.init();
	}

	init() {
		this.$field = this.$container.querySelector(this.selectorField);
		this.$result = this.$container.querySelector(this.selectorResult);
		if (!this.$field || !this.$result) return;

		this.$clearResult = this.$container.querySelector(this.selectorClearResult);
		this.$historyResults = this.$container.querySelector(this.selectorResultHistory);
		this.$clearHistory = this.$container.querySelector(this.selectorClearHistory);
		this.$searchItems = this.$container.querySelectorAll(this.selectorSearchItem);
		this.$removeItemButtons = this.$container.querySelectorAll(this.selectorRemoveItemButton);

		const formCallbacks = { onSubmit: this.callbacks.onSubmit };
		let formInstance = classInstance.get(this.$container)?.form;
		if (!formInstance) {
			formInstance = new Form(this.$container, formCallbacks);
		} else {
			formInstance.addCallbacks(formCallbacks);
		}
		this.form = formInstance;

		this.bindEvents();
		this.setupRunSearch();
	}

	bindEvents() {
		this.$field.addEventListener('input', this.handleInput);
		this.$clearResult?.addEventListener('click', this.handleClearResultsClick);
		this.$clearHistory?.addEventListener('click', this.handleClearHistoryClick);
		this.$result.addEventListener('click', this.handleRemoveItemClick);
		this.$historyResults?.addEventListener('click', this.handleRemoveItemClick);
	}

	handleInput = () => {
		const value = this.$field.value;

		this.callbacks?.onInput?.(value);
		this.runSearch(value);
	};

	getRequestMode(endpoint) {
		if (this.requestMode !== 'auto') {
			return this.requestMode;
		}

		if (endpoint.endsWith('.json') || endpoint.endsWith('.html')) {
			return 'get';
		}

		return 'post';
	}

	setupRunSearch() {
		const debouncedSearch = debounce(async (query) => {
			const currentRequestId = ++this.requestCounter;

			try {
				const html = await this.getResults(query);

				if (currentRequestId !== this.requestCounter) {
					return;
				}

				if (!html || !html.trim()) {
					this.clearResults();
					return;
				}

				this.renderResults(html);
				this.highlightResults(query);
			} catch (error) {
				if (error?.name === 'AbortError') return;

				console.error('Search request failed:', error);
				this.clearResults();
			}
		}, this.debounceDelay);

		this.runSearch = (query) => {
			const trimmedQuery = query.trim();

			if (!trimmedQuery.length) {
				this.abortPendingRequest();
				debouncedSearch.cancel?.();
				this.clearResults();
				return;
			}

			if (trimmedQuery.length < this.minQueryLength) {
				this.abortPendingRequest();
				debouncedSearch.cancel?.();
				this.clearResults();
				return;
			}

			debouncedSearch(trimmedQuery);
		};
	}

	async getResults(query) {
		if (this.searchProvider) {
			return await this.searchProvider(query);
		}

		return await this.fetchResults(query);
	}

	setSearchProvider(provider) {
		this.searchProvider = provider;
	}

	abortPendingRequest() {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	async fetchResults(query, endpoint = this.dataUrl) {
		this.abortPendingRequest();

		this.abortController = new AbortController();
		const { signal } = this.abortController;

		const mode = this.getRequestMode(endpoint);

		try {
			let response;

			if (mode === 'get') {
				const url = `${endpoint}?q=${encodeURIComponent(query)}`;
				response = await fetch(url, { signal });
			} else {
				const defaultHeaders = {
					'Accept': 'text/html',
					'Content-Type': 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				};

				response = await fetch(endpoint, {
					method: 'POST',
					headers: this.postHeaders ?? defaultHeaders,
					body: JSON.stringify({
						...(this.postBody ?? {}),
						q: query.trim(),
						l: this.minQueryLength,
						ajax_call: 'y',
					}),
					signal,
				});
			}

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			return await response.text();
		} finally {
			this.abortController = null;
		}
	}

	renderResults(html) {
		this.$result.innerHTML = html;
		initSearchResults(this.$result);
		this.$container?.dispatchEvent(new CustomEvent('search:renderResults', { detail: { results: html }, bubbles: true }));
	}

	handleClearResultsClick = (e) => {
		e.preventDefault();
		this.clearResults();
		this.$field.value = '';
		this.$field.focus();
	};

	clearResults = () => {
		this.$result.innerHTML = '';
		this.$container?.dispatchEvent(new CustomEvent('search:clearResults', { bubbles: true }));
	};

	addOptions(options = {}) {
		if (options.requestMode) this.requestMode = options.requestMode;
		if (options.dataUrl) this.dataUrl = options.dataUrl;
		if (options.minQueryLength) this.minQueryLength = options.minQueryLength;

		if (options.debounceDelay) {
			this.debounceDelay = options.debounceDelay;
			this.setupRunSearch();
		}

		if (options.postHeaders !== undefined) this.postHeaders = options.postHeaders;
		if (options.postBody !== undefined) this.postBody = options.postBody;
		if (options.searchProvider !== undefined) this.searchProvider = options.searchProvider;
	}

	addCallbacks = (callbacks) => {
		this.callbacks = { ...this.callbacks, ...callbacks };
		if (callbacks.onSubmit) {
			this.form.addCallbacks({ onSubmit: callbacks.onSubmit });
		}
	};

	highlightResults = (query) => {
		if (!query) return;

		const container = this.$result.querySelector('.js-search__highlight-items');
		if (!container) return;

		const highlighted = container.querySelectorAll('mark.search-highlight');
		highlighted.forEach((el) => {
			el.replaceWith(document.createTextNode(el.textContent));
		});

		const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

		const walk = (node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				if (node.textContent.trim() !== '') {
					const parent = node.parentElement;
					if (!parent.closest('.js-search__no-highlight')) {
						const frag = document.createDocumentFragment();
						let lastIndex = 0;
						let match;

						while ((match = regex.exec(node.textContent)) !== null) {
							if (match.index > lastIndex) {
								frag.appendChild(document.createTextNode(node.textContent.slice(lastIndex, match.index)));
							}

							const mark = document.createElement('mark');
							mark.className = 'search-highlight';
							mark.textContent = match[0];
							frag.appendChild(mark);

							lastIndex = match.index + match[0].length;
						}

						if (lastIndex < node.textContent.length) {
							frag.appendChild(document.createTextNode(node.textContent.slice(lastIndex)));
						}

						node.replaceWith(frag);
					}
				}
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				node.childNodes.forEach(walk);
			}
		};

		container.childNodes.forEach(walk);
	};

	handleClearHistoryClick = () => {
		if (!this.$historyResults) return;
		this.$container.dispatchEvent(new CustomEvent('search:clearHistory'));
	};

	handleRemoveItemClick = (e) => {
		e.stopPropagation();
		const button = e.target.closest(this.selectorRemoveItemButton);
		if (!button) return;

		const item = button.closest(this.selectorSearchItem);
		this.$container.dispatchEvent(new CustomEvent('search:removeItem', { detail: { item } }));
	};
}